/**
 * Injected canister / network config for mobile (and any non-frontend consumer).
 * Avoids hard-coding Vite import.meta.env inside the library.
 */

export type NetworkLabel = "local" | "play" | "production";

export type LedgerIds = {
	usdm?: string;
	mmk?: string;
	icp?: string;
	ckBtc?: string;
	ckEth?: string;
	ckUsdc?: string;
};

export type IcClientConfig = {
	/** protocol canister principal text */
	protocolCanisterId: string;
	/** HttpAgent host, e.g. https://icp-api.io */
	host: string;
	/** local | play | production */
	network: NetworkLabel;
	/** Internet Identity authorize URL */
	identityProvider: string;
	/** Optional II derivation origin (window origin when omitted by caller) */
	derivationOrigin?: string;
	/** Whether to fetch root key (local replica only) */
	fetchRootKey: boolean;
	ledgers: LedgerIds;
	/** Full web app URL for “open desktop features” */
	webAppUrl?: string;
};

export const MAINNET_II_URL = "https://id.ai/authorize";
export const MAINNET_IC_HOST = "https://icp-api.io";

/** Mainnet production ledger principals (ICP, ck*). */
export const MAINNET_LEDGERS = {
	icp: "ryjl3-tyaaa-aaaaa-aaaba-cai",
	ckBtc: "mxzaz-hqaaa-aaaar-qaaca-cai",
	ckEth: "ss2fx-dyaaa-aaaar-qacoq-cai",
	ckUsdc: "xevnm-gaaaa-aaaar-qafnq-cai",
} as const;

/** Build config from a flat env map (Vite import.meta.env or process.env). */
export function configFromEnv(
	env: Record<string, string | undefined>,
	overrides: Partial<IcClientConfig> = {},
): IcClientConfig {
	const appEnv = (env.VITE_APP_ENV ?? env.MODE ?? "production").toLowerCase();
	const icpEnv = (
		env.VITE_ICP_ENVIRONMENT ??
		env.ICP_ENVIRONMENT ??
		""
	).toLowerCase();

	let network: NetworkLabel = "production";
	if (appEnv === "demo-play" || appEnv === "play" || icpEnv === "play") {
		network = "play";
	} else if (
		appEnv === "development" ||
		appEnv === "local" ||
		icpEnv === "local" ||
		icpEnv === "development"
	) {
		network = "local";
	}

	const protocolCanisterId =
		env.VITE_PROTOCOL_CANISTER_ID ??
		env.VITE_CANISTER_ID_PROTOCOL ??
		overrides.protocolCanisterId ??
		"";

	const host =
		env.VITE_IC_HOST &&
		!env.VITE_IC_HOST.includes("127.0.0.1") &&
		!env.VITE_IC_HOST.includes("localhost") &&
		network !== "local"
			? env.VITE_IC_HOST
			: network === "local"
				? (env.VITE_IC_HOST ?? "http://127.0.0.1:8000")
				: MAINNET_IC_HOST;

	const identityProvider =
		env.VITE_II_URL?.trim() ||
		(network === "local" && env.VITE_USE_MAINNET_II === "0"
			? undefined
			: MAINNET_II_URL) ||
		MAINNET_II_URL;

	const base: IcClientConfig = {
		protocolCanisterId,
		host,
		network,
		identityProvider,
		derivationOrigin: env.VITE_DERIVATION_ORIGIN,
		fetchRootKey: network === "local",
		ledgers: {
			usdm: env.VITE_CANISTER_ID_USDM_LEDGER,
			mmk: env.VITE_CANISTER_ID_MMK_LEDGER,
			icp:
				env.VITE_ICP_LEDGER_CANISTER_ID ??
				env.VITE_CANISTER_ID_DEMO_ICP_LEDGER ??
				(network === "production" ? MAINNET_LEDGERS.icp : undefined),
			ckBtc:
				env.VITE_ckBTC_LEDGER_CANISTER_ID ??
				env.VITE_CANISTER_ID_DEMO_CKBTC_LEDGER ??
				(network === "production" ? MAINNET_LEDGERS.ckBtc : undefined),
			ckEth:
				env.VITE_ckETH_LEDGER_CANISTER_ID ??
				env.VITE_CANISTER_ID_DEMO_CKETH_LEDGER ??
				(network === "production" ? MAINNET_LEDGERS.ckEth : undefined),
			ckUsdc:
				env.VITE_ckUSDC_LEDGER_CANISTER_ID ??
				env.VITE_CANISTER_ID_DEMO_CKUSDC_LEDGER ??
				(network === "production" ? MAINNET_LEDGERS.ckUsdc : undefined),
		},
		webAppUrl: env.VITE_WEB_APP_URL,
	};

	return {
		...base,
		...overrides,
		ledgers: {
			...base.ledgers,
			...overrides.ledgers,
		},
	};
}

export function assertConfig(config: IcClientConfig): void {
	if (!config.protocolCanisterId?.trim()) {
		throw new Error(
			"Missing protocol canister id (VITE_PROTOCOL_CANISTER_ID)",
		);
	}
}
